import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import saveAs from 'file-saver';
import { Observable, concatMap, from, of } from 'rxjs';
import { Company, SubNationalDivision, User } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { DateRangeSelectionModalComponent } from 'src/app/modals/date-range-selection-modal/date-range-selection-modal.component';
import { SelectClientsModalComponent } from 'src/app/modals/select-clients-modal/select-clients-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ReportService } from 'src/app/services/report.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

type ReportOptions = {
  name: string;
  fileName: string;
  dateRange?: boolean;
  selectCompanies?: boolean;
  selectStates?: boolean;
  selectYear?: boolean;
  reportFn: (params?: any) => Observable<ArrayBuffer>;
};

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ReportsComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private ReportService: ReportService,
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private FormBuilder: FormBuilder,
    private UtilityService: UtilityService
  ) {
    this.BusinessReportOptionsForm.get('year')?.valueChanges.subscribe({
      next: (changes) => {
        this.selectedYear = changes;
        this.createBusinessReports();
      }
    });
  }

  LoggedInUser!: User;
  years: number[] = Array.from(
    { length: new Date().getFullYear() - 2017 + 2 },
    (_, i) => 2017 + i
  );
  selectedYear?: number;

  Reports?: ReportOptions[];
  BusinessReports?: ReportOptions[];
  BusinessReportOptionsForm: FormGroup = this.FormBuilder.group({
    year: [null, [Validators.required]]
  });

  createReports() {
    this.Reports = [
      {
        name: 'Payment Detail Report',
        fileName: 'PaymentDetailReport.xlsx',
        selectCompanies: true,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generatePaymentDetailReport(params)
      },
      {
        name: 'Payment Summary Report',
        fileName: 'PaymentSummaryReport.xlsx',
        selectCompanies: true,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateVendorPaymentSummaryReport(params)
      },
      {
        name: 'Fee Voucher Report',
        fileName: 'FeeVoucherReport.xlsx',
        selectCompanies: true,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateFeeVouchersReport(params)
      },
      {
        name: 'Client Fee List',
        fileName: 'ClientFeeList.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateClientFeeListReport(params)
      },
      {
        name: 'All Compliance',
        fileName: 'AllCompliance.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateComplianceDetailReport(params)
      },
      {
        name: 'All Findings',
        fileName: 'AllFindings.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateComplianceSummaryReport(params)
      },
      {
        name: 'All Compliance Failed And Missing Summary',
        fileName: 'AllCompliance-FailedAndMissing.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateComplianceSummaryReport(params)
      },
      {
        name: 'All Compliance Failed - Business Verified Vendors Only',
        fileName: 'AllCompliance-BV.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateComplianceSummaryBVOnlyReport(params)
      },
      {
        name: 'Insurance Results',
        fileName: 'InsuranceResults.xlsx',
        selectCompanies: true,
        reportFn: (params) =>
          this.ReportService.generateInsuranceResultsReport(params)
      },
      {
        name: 'All Active Vendors',
        fileName: 'AllActiveVendors.xlsx',
        selectCompanies: true,
        dateRange: true,
        selectStates: true,
        reportFn: (params) =>
          this.ReportService.generateAllActiveVendorsReport(params)
      },

      {
        name: 'Business Verified Invoices',
        fileName: 'BusinessVerifiedInvoices.xlsx',
        selectCompanies: true,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateBusinessVerifiedInvoicesReport(params)
      },
      {
        name: 'Business Verification Override',
        fileName: 'BusinessVerificationOverride.xlsx',
        selectCompanies: true,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateBusinessVerificationOverrideReport(params)
      },
      {
        name: 'FSR KPI',
        fileName: 'FSRKPI.xlsx',
        selectCompanies: false,
        dateRange: true,
        reportFn: (params) => this.ReportService.generateFSRKPIReport(params)
      },
      {
        name: 'FSR KPI Quarterly',
        fileName: 'FSRKPIQuarterly.xlsx',
        selectCompanies: false,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateFSRKPIQuarterlyReport(params)
      },

      {
        name: 'FSR Conflict of Interest',
        fileName: 'ConflictOfInterest.xlsx',
        selectCompanies: false,
        dateRange: true,
        reportFn: (params) =>
          this.ReportService.generateConflictOfInterestReport(params)
      }
    ];
  }

  createBusinessReports() {
    this.BusinessReports = [
      {
        name: 'Client Net Income Report',
        fileName: `ClientNetIncomeReport${this.selectedYear}.xlsx`,
        selectYear: true,
        reportFn: (params) =>
          this.ReportService.generateClientNetIncomeReport(params)
      }
    ];
  }

  generateReport(report: ReportOptions) {
    const { selectCompanies, dateRange, selectYear } = report;
    const ReportQuery: GenericObject = {};
    (() => {
      if (selectCompanies) {
        const { componentInstance, result } = this.ModalService.open(
          SelectClientsModalComponent,
          { backdrop: 'static', size: 'md' }
        );

        if (report.selectStates) {
          Object.assign(componentInstance, {
            SelectStates: report.selectStates
          });
        }

        return from(result).pipe(
          concatMap(
            ({
              selectedCompanies,
              selectedStates
            }: {
              selectedCompanies: Company[];
              selectedStates: SubNationalDivision[];
            }) => {
              if (selectedCompanies.length === 0) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Cannot generate report. Need to select at least 1 company.',
                  { returnError: true }
                );
              }

              const CompanyIds = selectedCompanies.map((c) => c.id);
              const SubNationalDivisionIds = (selectedStates || []).map(
                (s) => s.id
              );

              return of(
                Object.assign(ReportQuery, {
                  CompanyIds,
                  SubNationalDivisionIds,
                  CompanyId: JSON.stringify({ $in: CompanyIds })
                })
              );
            }
          )
        );
      }

      return of(ReportQuery);
    })().subscribe({
      next: () => {
        (() => {
          if (dateRange) {
            const { result } = this.ModalService.open(
              DateRangeSelectionModalComponent,
              { backdrop: 'static', size: 'md' }
            );
            return from(result).pipe(
              concatMap(
                ({
                  startDate,
                  endDate
                }: {
                  startDate: Date;
                  endDate: Date;
                }) => {
                  return of(Object.assign(ReportQuery, { startDate, endDate }));
                }
              )
            );
          } else if (selectYear) {
            const errors = this.UtilityService.generateValidationErrorsObject(
              this.BusinessReportOptionsForm
            );

            if (errors) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing required information to generate form.',
                { returnError: true }
              );
            }

            if (!this.selectedYear) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing selected year.',
                { returnError: true }
              );
            }

            return of(
              Object.assign(ReportQuery, { SelectedYear: this.selectedYear })
            );
          }

          return of(ReportQuery);
        })().subscribe({
          next: (ReportQuery) => {
            report.reportFn(ReportQuery).subscribe({
              next: (Report: BlobPart) =>
                saveAs(new Blob([Report]), report.fileName)
            });
          }
        });
      }
    });
  }

  generateBusinessReport(report: ReportOptions) {
    const ReportQuery = {};

    if (report.selectYear && !report.dateRange) {
      const errors = this.UtilityService.generateValidationErrorsObject(
        this.BusinessReportOptionsForm
      );

      if (errors) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing required information to generate form.',
          { returnError: true }
        );
      }

      if (!this.selectedYear) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing selected year.',
          { returnError: true }
        );
      }

      Object.assign(ReportQuery, { SelectedYear: this.selectedYear });
    }

    report.reportFn(ReportQuery).subscribe({
      next: (Report: BlobPart) => saveAs(new Blob([Report]), report.fileName)
    });
  }

  ngOnInit(): void {
    this.LoggedInUser = this.AuthService.getUser();

    this.createReports();
    this.createBusinessReports();
  }
}
